import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import {
  CadsGlobalModalService,
  cleanCadsMainTemplateFbEventName,
  CADS_MAIN_TEMPLATE_ANALYTICS,
  type CadsMainTemplateModalConfig,
} from '@brightside/angular-cads';
import {
  ClientPreferencesService,
} from '../client-preferences/client-preferences.service';
import { take } from 'rxjs';

/** Opens the SMS marketing consent prompt as a CADS dialog on the home feed. */
@Injectable({ providedIn: 'root' })
export class SmsPromptService {
  private readonly _clientPreferences = inject(ClientPreferencesService);
  private readonly _translate = inject(TranslateService);
  private readonly _cadsGlobalModal = inject(CadsGlobalModalService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _analytics = inject(CADS_MAIN_TEMPLATE_ANALYTICS, { optional: true });

  private readonly _submitting = signal(false);
  private _activeModal: CadsMainTemplateModalConfig | null = null;

  /** Fetches preferences and opens the dialog when `smsMarketingConsent.promptForValue` is true. */
  showIfNeeded(): void {
    if (this._cadsGlobalModal.isOpen()) {
      return;
    }
    this._clientPreferences
      .getPreferences()
      .pipe(take(1), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (items) => {
          if (this._clientPreferences.shouldPromptForSmsMarketing(items)) {
            this.open();
          }
        },
        error: () => {
          // Scenario 1b: GET failure — proceed to feed without blocking.
        },
      });
  }

  /** Opens the SMS prompt dialog (dev menu / direct preview). */
  open(): void {
    if (this._cadsGlobalModal.isOpen()) {
      return;
    }
    this._submitting.set(false);

    const confirmationState = { checked: false };
    const modal: CadsMainTemplateModalConfig = {
      size: 'medium',
      showCloseButton: false,
      closeOnBackdropClick: false,
      closeOnEscape: false,
      template: {
        density: 'full',
        heading: {
          title: this._translate.instant('SMS_PROMPT_TITLE'),
        },
        primaryCta: {
          skipNavigation: true,
          button: {
            label: this._translate.instant('BUTTON_CONTINUE'),
            style: 'primary',
          },
        },
        analytics: { event_name: 'sms_prompt' },
      },
    };
    this._activeModal = modal;

    this._cadsGlobalModal.open({
      modal,
      simpleBody: {
        text: this._translate.instant('SMS_PROMPT_DESC'),
        html: true,
        heroIcon: { icon: 'chat', shape: 'circle', size: 'large', color: 'var(--cads-color-blue)' },
        confirmation: {
          label: this._translate.instant('SMS_PROMPT_CHECKBOX'),
          html: true,
          initiallyChecked: false,
        },
      },
      confirmationState,
      primaryTapped: () => {
        this._onContinue(confirmationState.checked);
      },
    });
  }

  private _onContinue(optInChecked: boolean): void {
    if (this._submitting()) {
      return;
    }
    this._logTap();
    this._submitPreferences(optInChecked);
  }

  private _submitPreferences(optInChecked: boolean): void {
    this._submitting.set(true);
    this._setPrimaryProcessing(true);
    this._clientPreferences
      .updatePreferences([{ key: 'smsMarketingConsent', booleanValue: optInChecked }])
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this._submitting.set(false);
          this._activeModal = null;
          this._cadsGlobalModal.close();
        },
        error: () => {
          this._submitting.set(false);
          this._setPrimaryProcessing(false);
          this._showSubmitError(optInChecked);
        },
      });
  }

  private _showSubmitError(optInChecked: boolean): void {
    this._activeModal = null;
    this._cadsGlobalModal.createGenericErrorModal({
      eventName: 'sms_prompt_submit',
      primaryAction: () => this._submitPreferences(optInChecked),
      dismissedAction: () => {
        this._cadsGlobalModal.close();
      },
    });
  }

  private _setPrimaryProcessing(processing: boolean): void {
    const activeModal = this._activeModal;
    const primaryCta = activeModal?.template.primaryCta;
    if (!activeModal || !primaryCta) {
      return;
    }
    const { template } = activeModal;
    const updatedModal: CadsMainTemplateModalConfig = {
      ...activeModal,
      template: {
        ...template,
        primaryCta: {
          ...primaryCta,
          button: {
            ...primaryCta.button,
            processing,
          },
        },
      },
    };
    this._activeModal = updatedModal;
    this._cadsGlobalModal.updateModal(updatedModal);
  }

  private _logTap(): void {
    const base = cleanCadsMainTemplateFbEventName('sms_prompt');
    this._analytics?.logEvent(`${base} tapped`);
  }
}
